import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { articles } from "@/mocks/carriers";

const articleGroups = [
  {
    id: "all",
    label: "すべて",
    title: "役立つ記事をすべて見る",
    description: "料金の見直しから端末購入、乗り換えまでまとめて確認できます。",
    categories: [] as string[],
  },
  {
    id: "cost",
    label: "料金を見直す",
    title: "料金を見直す・比べる",
    description: "請求の見方、データ容量、料金比較から、払いすぎを見つけます。",
    categories: ["料金の見直し", "料金比較", "データ容量"],
  },
  {
    id: "device",
    label: "端末を選ぶ",
    title: "端末を安く買う・長く使う",
    description: "iPhoneの購入、2年返却、中古端末、修理と買い替えを具体的に比べます。",
    categories: ["端末購入", "中古端末", "修理・買い替え"],
  },
  {
    id: "switching",
    label: "乗り換えを知る",
    title: "乗り換え前に知っておくこと",
    description: "手続きの前に、端末残債や家族割などの見落としやすい点を確認します。",
    categories: ["はじめての乗り換え"],
  },
];
const concerns = [
  { label: "スマホ代が高い", text: "請求を4つに分ける", to: "/articles/bill-basics" },
  { label: "2年返却が気になる", text: "月額と総額を比べる", to: "/articles/return-program" },
  { label: "iPhone 18 Proを買う", text: "購入先とMNPを比べる", to: "/articles/iphone-18-pro-mnp" },
  { label: "修理か買い替えか", text: "3つの基準で判断", to: "/articles/iphone-repair-or-replace" },
];

const pinnedArticleIds = ["ahamo-guide", "iijmio-guide", "iijmio-iphone-no-return", "rakuten-guide", "ymobile-guide", "uqmobile-guide"];
const pinnedArticles = pinnedArticleIds
  .map((id) => articles.find((article) => article.id === id))
  .filter((article): article is (typeof articles)[number] => Boolean(article));

export default function Articles() {
  const [groupId, setGroupId] = useState("all");
  const activeGroup = articleGroups.find((group) => group.id === groupId) || articleGroups[0];
  const list = (groupId === "all" ? articles : articles.filter((article) => activeGroup.categories.includes(article.category)))
    .filter((article) => !pinnedArticleIds.includes(article.id));
  const [featured, ...rest] = list;

  return (
    <Layout>
      <section className="border-b border-background-300">
        <div className="mx-auto max-w-[1100px] px-4 py-14 md:px-6 md:py-20">
          <div className="text-xs font-semibold tracking-[0.3em] text-secondary-600">ARTICLES</div>
          <h1 className="font-editorial mt-4 text-3xl font-bold leading-tight text-foreground-900 md:text-5xl">
            スマホ代と乗り換えを、<br className="hidden md:block" />
            やさしく整理する記事
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            「安い会社はどこ？」より先に、いま困っていることから選べます。料金・端末・修理・乗り換えを、判断できる形に整理しています。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-4 pt-8 md:px-6 md:pt-10">
        <div className="mb-4 text-sm font-bold text-foreground-900">いま気になることから選ぶ</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {concerns.map((item) => (
            <Link key={item.to} to={item.to} className="group rounded-lg border border-background-300 bg-background-50 p-4 hover:border-primary-500">
              <div className="text-sm font-bold text-foreground-900 group-hover:text-primary-600">{item.label}</div>
              <div className="mt-2 flex items-center justify-between text-xs text-foreground-600">
                <span>{item.text}</span><span aria-hidden="true">→</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-4 pt-10 md:px-6 md:pt-12">
        <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-5 md:p-7">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-xs font-semibold tracking-[0.18em] text-primary-600">PICK UP</div>
              <h2 className="font-editorial mt-2 text-xl font-bold text-foreground-900 md:text-2xl">申込み先を具体的に検討したい方へ</h2>
            </div>
            <p className="text-xs leading-6 text-foreground-600">料金・通話・端末購入まで、各社の特徴を具体的に解説</p>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {pinnedArticles.map((article) => (
              <Link key={article.id} to={`/articles/${article.id}`} className="group overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm hover:border-primary-500">
                <div className="relative aspect-[16/9] overflow-hidden bg-background-200">
                  <img src={article.image} alt={article.title} className="h-full w-full object-cover object-top transition-transform group-hover:scale-[1.03]" />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-bold text-white">おすすめ</span>
                </div>
                <div className="p-4">
                  <div className="text-[11px] font-semibold text-primary-600">{article.category}</div>
                  <h3 className="mt-2 text-[15px] font-bold leading-6 text-foreground-900 group-hover:text-primary-600">{article.title}</h3>
                  <p className="mt-2 line-clamp-2 text-[13px] leading-6 text-foreground-600">{article.excerpt}</p>
                  <div className="mt-4 text-xs font-bold text-primary-600">詳しく見る →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-4 pt-8 md:px-6 md:pt-10">
        <div className="mb-3 text-xs font-semibold tracking-[0.18em] text-secondary-600">記事を目的別に探す</div>
        <div className="flex flex-wrap gap-2">
          {articleGroups.map((item) => (
            <button
              key={item.id}
              onClick={() => setGroupId(item.id)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium ${
                groupId === item.id
                  ? "border-primary-500 bg-primary-500 text-white"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-500"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 border-l-4 border-primary-500 pl-4">
          <h2 className="font-editorial text-xl font-bold text-foreground-900 md:text-2xl">{activeGroup.title}</h2>
          <p className="mt-1 text-sm leading-6 text-foreground-600">{activeGroup.description}</p>
        </div>
        {featured && (
          <Link to={`/articles/${featured.id}`} className="group block cursor-pointer">
            <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-300 bg-background-50 md:grid-cols-2">
              <div className="aspect-[16/10] overflow-hidden bg-background-200 md:aspect-auto md:h-full">
                <img src={featured.image} alt={featured.title} className="h-full w-full object-cover object-top" />
              </div>
              <div className="flex flex-col justify-center p-6 md:p-8">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                  {featured.category}
                </div>
                <h2 className="font-editorial mt-4 text-xl font-bold leading-snug text-foreground-900 group-hover:text-primary-600 md:text-2xl">
                  {featured.title}
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{featured.excerpt}</p>
                <div className="mt-5 flex items-center gap-4 text-xs text-foreground-500">
                  <span>{featured.date}</span>
                  <span>読了 {featured.readTime}</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((article) => (
            <Link key={article.id} to={`/articles/${article.id}`} className="group cursor-pointer">
              <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50">
                <div className="aspect-[16/10] overflow-hidden bg-background-200">
                  <img src={article.image} alt={article.title} className="h-full w-full object-cover object-top transition-transform group-hover:scale-[1.03]" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="text-[11px] font-semibold text-primary-600">{article.category}</div>
                  <div className="mt-2 text-[15px] font-bold leading-6 text-foreground-900 group-hover:text-primary-600">{article.title}</div>
                  <div className="mt-2 line-clamp-2 text-[13px] leading-6 text-foreground-600">{article.excerpt}</div>
                  <div className="mt-auto flex items-center gap-3 pt-4 text-[11px] text-foreground-500">
                    <span>{article.date}</span>
                    <span>読了 {article.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
